/**
 * Dynamic block raw-name → human-readable label translation table
 * (Phase L.3).
 *
 * Many DXF files (especially older / legacy ones) carry dynamic-block
 * customProperties whose keys are the raw English AutoCAD parameter
 * names — `Position1`, `VisibilityState1`, `FlipState1`, `Distance1`,
 * `Angle1`, `Rotation1`, `Lookup1`, `Origin X`, `Scale1`, etc.
 *
 * When the block has no typed `dynamicMetadata.parameters` AND the
 * upstream backfill (Phase L.1's `inferDynamicMetadata`) cannot map a
 * key to a typed parameter, the Inspector still has to show SOME label.
 * Without translation it would surface the raw English name in the
 * Russian UI, which violates CLAUDE.md `i18n: интерфейс ВСЕГДА на двух
 * языках` and the user's report:
 *
 *   > при выделении динамического блока многие свойства в отличие от
 *   > автокада выглядят в виде странных английских названий и править
 *   > их можно через поле ввода
 *
 * This module supplies a lookup-first / regex-fallback translator. The
 * direct map covers the well-known fixed AutoCAD names; the regex
 * fallback handles the open-ended numeric variants (`Position7`,
 * `VisibilityState12`, …) that AutoCAD generates when the user adds a
 * second / third / Nth parameter of the same kind in the Block Editor.
 *
 * Names of *typed* parameters (those carried by `dynamicMetadata.
 * parameters` with their own author-defined `name` / `label`) are NOT
 * routed through this module — they are taken verbatim from the metadata
 * since the author already chose them. This module only handles the
 * RAW-mode fallback path.
 */

import type { LanguageCode } from "./messages";

/**
 * Direct lookup of the most common raw AutoCAD parameter names. ~85
 * entries — covers AutoCAD's defaults for parameter indices 1…6 plus
 * the Origin X/Y / Scale / Distance / Angle / Rotation / Lookup /
 * Visibility / Flip / Position families.
 *
 * Entries beyond index 6 are produced by the regex fallback so we don't
 * pay for an exhaustive map; AutoCAD blocks rarely carry more than 4
 * parameters of the same kind in real-world DXFs.
 */
export const DYNAMIC_BLOCK_LABEL_MAP: Record<
  string,
  { ru: string; en: string }
> = {
  // ─── Position parameters (XY pair: <name> X / <name> Y or singular <name>) ───
  Position1: { ru: "Координата 1", en: "Position 1" },
  Position2: { ru: "Координата 2", en: "Position 2" },
  Position3: { ru: "Координата 3", en: "Position 3" },
  Position4: { ru: "Координата 4", en: "Position 4" },
  Position5: { ru: "Координата 5", en: "Position 5" },
  Position6: { ru: "Координата 6", en: "Position 6" },
  "Position1 X": { ru: "Координата 1: X", en: "Position 1: X" },
  "Position1 Y": { ru: "Координата 1: Y", en: "Position 1: Y" },
  "Position2 X": { ru: "Координата 2: X", en: "Position 2: X" },
  "Position2 Y": { ru: "Координата 2: Y", en: "Position 2: Y" },
  "Position3 X": { ru: "Координата 3: X", en: "Position 3: X" },
  "Position3 Y": { ru: "Координата 3: Y", en: "Position 3: Y" },
  "Position4 X": { ru: "Координата 4: X", en: "Position 4: X" },
  "Position4 Y": { ru: "Координата 4: Y", en: "Position 4: Y" },
  "Position5 X": { ru: "Координата 5: X", en: "Position 5: X" },
  "Position5 Y": { ru: "Координата 5: Y", en: "Position 5: Y" },
  "Position6 X": { ru: "Координата 6: X", en: "Position 6: X" },
  "Position6 Y": { ru: "Координата 6: Y", en: "Position 6: Y" },
  // ─── Visibility states ───
  VisibilityState1: { ru: "Видимость 1", en: "Visibility 1" },
  VisibilityState2: { ru: "Видимость 2", en: "Visibility 2" },
  VisibilityState3: { ru: "Видимость 3", en: "Visibility 3" },
  VisibilityState4: { ru: "Видимость 4", en: "Visibility 4" },
  VisibilityState5: { ru: "Видимость 5", en: "Visibility 5" },
  VisibilityState6: { ru: "Видимость 6", en: "Visibility 6" },
  Visibility: { ru: "Видимость", en: "Visibility" },
  Visibility1: { ru: "Видимость 1", en: "Visibility 1" },
  Visibility2: { ru: "Видимость 2", en: "Visibility 2" },
  // ─── Flip parameters (boolean mirror) ───
  FlipState1: { ru: "Отражение 1", en: "Flip State 1" },
  FlipState2: { ru: "Отражение 2", en: "Flip State 2" },
  FlipState3: { ru: "Отражение 3", en: "Flip State 3" },
  FlipState4: { ru: "Отражение 4", en: "Flip State 4" },
  FlipState5: { ru: "Отражение 5", en: "Flip State 5" },
  FlipState6: { ru: "Отражение 6", en: "Flip State 6" },
  Flip1: { ru: "Отражение 1", en: "Flip 1" },
  Flip2: { ru: "Отражение 2", en: "Flip 2" },
  Flip3: { ru: "Отражение 3", en: "Flip 3" },
  // ─── Linear distance parameters ───
  Distance1: { ru: "Расстояние 1", en: "Distance 1" },
  Distance2: { ru: "Расстояние 2", en: "Distance 2" },
  Distance3: { ru: "Расстояние 3", en: "Distance 3" },
  Distance4: { ru: "Расстояние 4", en: "Distance 4" },
  Distance5: { ru: "Расстояние 5", en: "Distance 5" },
  Distance6: { ru: "Расстояние 6", en: "Distance 6" },
  Linear1: { ru: "Линейный 1", en: "Linear 1" },
  Linear2: { ru: "Линейный 2", en: "Linear 2" },
  Linear3: { ru: "Линейный 3", en: "Linear 3" },
  // ─── Angle parameters (paired with Distance to form polar) ───
  Angle1: { ru: "Угол 1", en: "Angle 1" },
  Angle2: { ru: "Угол 2", en: "Angle 2" },
  Angle3: { ru: "Угол 3", en: "Angle 3" },
  Angle4: { ru: "Угол 4", en: "Angle 4" },
  Angle5: { ru: "Угол 5", en: "Angle 5" },
  Angle6: { ru: "Угол 6", en: "Angle 6" },
  // ─── Rotation parameters (single angle around base) ───
  Rotation1: { ru: "Поворот 1", en: "Rotation 1" },
  Rotation2: { ru: "Поворот 2", en: "Rotation 2" },
  Rotation3: { ru: "Поворот 3", en: "Rotation 3" },
  Rotation4: { ru: "Поворот 4", en: "Rotation 4" },
  Rotation5: { ru: "Поворот 5", en: "Rotation 5" },
  Rotation6: { ru: "Поворот 6", en: "Rotation 6" },
  // ─── Lookup parameters ───
  Lookup1: { ru: "Поиск 1", en: "Lookup 1" },
  Lookup2: { ru: "Поиск 2", en: "Lookup 2" },
  Lookup3: { ru: "Поиск 3", en: "Lookup 3" },
  Lookup4: { ru: "Поиск 4", en: "Lookup 4" },
  Lookup5: { ru: "Поиск 5", en: "Lookup 5" },
  Lookup6: { ru: "Поиск 6", en: "Lookup 6" },
  // ─── Origin (basepoint override) ───
  Origin: { ru: "Базовая точка", en: "Origin" },
  "Origin X": { ru: "Базовая точка: X", en: "Origin: X" },
  "Origin Y": { ru: "Базовая точка: Y", en: "Origin: Y" },
  "Origin Z": { ru: "Базовая точка: Z", en: "Origin: Z" },
  // ─── Scale (lookup or linear scale driver) ───
  Scale1: { ru: "Масштаб 1", en: "Scale 1" },
  Scale2: { ru: "Масштаб 2", en: "Scale 2" },
  Scale3: { ru: "Масштаб 3", en: "Scale 3" },
  // ─── XY parameters ───
  XY1: { ru: "XY 1", en: "XY 1" },
  XY2: { ru: "XY 2", en: "XY 2" },
  // ─── Alignment parameters (auto-rotation) ───
  Alignment1: { ru: "Выравнивание 1", en: "Alignment 1" },
  Alignment2: { ru: "Выравнивание 2", en: "Alignment 2" },
  // ─── Point parameters ───
  Point1: { ru: "Точка 1", en: "Point 1" },
  Point2: { ru: "Точка 2", en: "Point 2" },
  Point3: { ru: "Точка 3", en: "Point 3" },
};

/**
 * Pattern-based fallbacks for raw names that:
 *   (a) match a known prefix family (Position / VisibilityState /
 *       Distance / Angle / Rotation / Lookup / Scale / Flip / Linear /
 *       Alignment / Point / XY / Visibility) but
 *   (b) carry an index outside the explicit 1…6 map.
 *
 * Order matters — more specific patterns (with `X|Y` axis suffix) run
 * before plain `<base><N>` so we don't accidentally translate
 * `Position7 X` as `Position 7` losing the axis.
 */
const PATTERN_FALLBACKS: ReadonlyArray<{
  re: RegExp;
  translate: (match: RegExpMatchArray, lang: LanguageCode) => string;
}> = [
  // Position{N} {X|Y|Z}
  {
    re: /^Position(\d+)\s+([XYZ])$/,
    translate: (m, lang) => {
      const idx = m[1]!;
      const axis = m[2]!;
      return lang === "ru"
        ? `Координата ${idx}: ${axis}`
        : `Position ${idx}: ${axis}`;
    },
  },
  // Position{N}
  {
    re: /^Position(\d+)$/,
    translate: (m, lang) =>
      lang === "ru" ? `Координата ${m[1]}` : `Position ${m[1]}`,
  },
  // VisibilityState{N}
  {
    re: /^VisibilityState(\d+)$/,
    translate: (m, lang) =>
      lang === "ru" ? `Видимость ${m[1]}` : `Visibility ${m[1]}`,
  },
  // Visibility{N}
  {
    re: /^Visibility(\d+)$/,
    translate: (m, lang) =>
      lang === "ru" ? `Видимость ${m[1]}` : `Visibility ${m[1]}`,
  },
  // FlipState{N}
  {
    re: /^FlipState(\d+)$/,
    translate: (m, lang) =>
      lang === "ru" ? `Отражение ${m[1]}` : `Flip State ${m[1]}`,
  },
  // Flip{N}
  {
    re: /^Flip(\d+)$/,
    translate: (m, lang) =>
      lang === "ru" ? `Отражение ${m[1]}` : `Flip ${m[1]}`,
  },
  // Distance{N}
  {
    re: /^Distance(\d+)$/,
    translate: (m, lang) =>
      lang === "ru" ? `Расстояние ${m[1]}` : `Distance ${m[1]}`,
  },
  // Linear{N}
  {
    re: /^Linear(\d+)$/,
    translate: (m, lang) =>
      lang === "ru" ? `Линейный ${m[1]}` : `Linear ${m[1]}`,
  },
  // Angle{N}
  {
    re: /^Angle(\d+)$/,
    translate: (m, lang) => (lang === "ru" ? `Угол ${m[1]}` : `Angle ${m[1]}`),
  },
  // Rotation{N}
  {
    re: /^Rotation(\d+)$/,
    translate: (m, lang) =>
      lang === "ru" ? `Поворот ${m[1]}` : `Rotation ${m[1]}`,
  },
  // Lookup{N}
  {
    re: /^Lookup(\d+)$/,
    translate: (m, lang) => (lang === "ru" ? `Поиск ${m[1]}` : `Lookup ${m[1]}`),
  },
  // Scale{N}
  {
    re: /^Scale(\d+)$/,
    translate: (m, lang) =>
      lang === "ru" ? `Масштаб ${m[1]}` : `Scale ${m[1]}`,
  },
  // Alignment{N}
  {
    re: /^Alignment(\d+)$/,
    translate: (m, lang) =>
      lang === "ru" ? `Выравнивание ${m[1]}` : `Alignment ${m[1]}`,
  },
  // Point{N}
  {
    re: /^Point(\d+)$/,
    translate: (m, lang) => (lang === "ru" ? `Точка ${m[1]}` : `Point ${m[1]}`),
  },
  // XY{N}
  {
    re: /^XY(\d+)$/,
    translate: (m, lang) => (lang === "ru" ? `XY ${m[1]}` : `XY ${m[1]}`),
  },
  // <base>.x / <base>.y / <base>.distance / <base>.angle suffixes
  // (compound parameters that the inspector did not group via the
  // typed-metadata path — fall back to a pretty per-suffix label).
  {
    re: /^(.+)\.x$/i,
    translate: (m, lang) =>
      lang === "ru" ? `${translateRawLabel(m[1]!, lang)}: X` : `${translateRawLabel(m[1]!, lang)}: X`,
  },
  {
    re: /^(.+)\.y$/i,
    translate: (m, lang) =>
      lang === "ru" ? `${translateRawLabel(m[1]!, lang)}: Y` : `${translateRawLabel(m[1]!, lang)}: Y`,
  },
  {
    re: /^(.+)\.distance$/i,
    translate: (m, lang) =>
      lang === "ru"
        ? `${translateRawLabel(m[1]!, lang)}: расстояние`
        : `${translateRawLabel(m[1]!, lang)}: distance`,
  },
  {
    re: /^(.+)\.angle$/i,
    translate: (m, lang) =>
      lang === "ru"
        ? `${translateRawLabel(m[1]!, lang)}: угол`
        : `${translateRawLabel(m[1]!, lang)}: angle`,
  },
];

/**
 * Translate a raw AutoCAD dynamic-block parameter name to a human-
 * readable label in the requested language. Returns the original key
 * unchanged when neither the direct map nor the regex fallbacks match
 * (so the user always sees SOMETHING — never an empty label).
 *
 * Algorithm:
 *   1. Direct lookup in `DYNAMIC_BLOCK_LABEL_MAP` (covers ~85 names).
 *   2. Regex fallbacks (Position{N}, VisibilityState{N}, …) — pattern
 *      order matters; X/Y axis suffix variants come first.
 *   3. Final fallback: the raw key as-is (prevents an empty cell).
 */
export function translateRawLabel(key: string, lang: LanguageCode): string {
  // 1. Direct lookup
  const direct = DYNAMIC_BLOCK_LABEL_MAP[key];
  if (direct) return direct[lang];
  // 2. Pattern fallbacks
  for (const { re, translate } of PATTERN_FALLBACKS) {
    const match = key.match(re);
    if (match) return translate(match, lang);
  }
  // 3. As-is fallback
  return key;
}
